"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const laravel_echo_1 = __importDefault(require("laravel-echo"));
const pusher_js_1 = __importDefault(require("pusher-js"));
const Journey_1 = require("../lib/Journey");
const useCurrentRef_1 = require("./useCurrentRef");
// Stops IDE from trying to cleanup.
if (pusher_js_1.default) {
    //
}
let echoSession = {
    pending: false,
    callbacks: [],
    echo: undefined,
};
function getEchoSession() {
    return new Promise((resolve, reject) => {
        var _a, _b;
        if (echoSession.pending) {
            echoSession.callbacks.push(resolve);
            return;
        }
        if (echoSession.echo) {
            resolve(echoSession);
            return;
        }
        echoSession = {
            pending: true,
            callbacks: [],
            echo: undefined,
        };
        (_b = (_a = Journey_1.Journey.config).echoProps) === null || _b === void 0 ? void 0 : _b.call(_a).then((echoProps) => {
            echoSession.echo = new laravel_echo_1.default(echoProps);
            echoSession.callbacks.forEach((cb) => cb(echoSession));
            resolve(echoSession);
            echoSession.pending = false;
            echoSession.callbacks = [];
        }).catch((error) => {
            reject(typeof error === 'string' ? new Error(error) : error);
        });
    });
}
function getChannel(type, channelId) {
    if (!echoSession.echo) {
        return null;
    }
    const channelInstance = type === 'private'
        ? echoSession.echo.private(channelId)
        : echoSession.echo.channel(channelId);
    return channelInstance;
}
/*
  Websockets, using Laravel Echo
 */
function useSocket(options) {
    const { onMessage, onError = (e) => console.error(e), onSubscribe, onStateChange, channelId, eventId, type } = options !== null && options !== void 0 ? options : {};
    const onMessageRef = useCurrentRef_1.useCurrentRef(onMessage);
    const onStateChangeRef = useCurrentRef_1.useCurrentRef(onStateChange);
    const onErrorRef = useCurrentRef_1.useCurrentRef(onError);
    const onSubscribeRef = useCurrentRef_1.useCurrentRef(onSubscribe);
    const onPusherSubscribeHandle = react_1.useCallback(() => {
        var _a;
        (_a = onSubscribeRef.current) === null || _a === void 0 ? void 0 : _a.call(onSubscribeRef);
    }, [onSubscribeRef]);
    const onPusherListenHandle = react_1.useCallback((args) => {
        var _a;
        (_a = onMessageRef.current) === null || _a === void 0 ? void 0 : _a.call(onMessageRef, args);
    }, [onMessageRef]);
    const onPusherChannelErrorHandle = react_1.useCallback((error) => {
        var _a;
        // If an error occurs trying to listen.
        (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, new Error(error.error));
    }, [onErrorRef]);
    const onPusherErrorHandle = react_1.useCallback((err) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const errorMessage = (_e = (_c = (_b = (_a = err.error) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : (_d = err.data) === null || _d === void 0 ? void 0 : _d.message) !== null && _e !== void 0 ? _e : `Pusher Error: ${(_h = (_g = (_f = err.error) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.code) !== null && _h !== void 0 ? _h : 'Unknown'}`;
        onErrorRef.current(new Error(errorMessage));
    }, [onErrorRef]);
    const onPusherStateChangeHandle = react_1.useCallback((states) => {
        var _a;
        (_a = onStateChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onStateChangeRef, states);
    }, [onStateChangeRef]);
    const disconnect = () => {
        /*
        This is absolutely needed due to callbacks not being unbinded on leave.
        If we don't use "stopListening()" and just use leave() it will not unbind the callback causing a double call.
        https://github.com/pusher/pusher-js/issues/273#issuecomment-692803569
        */
        const channel = getChannel(type, channelId);
        if (channel) {
            channel === null || channel === void 0 ? void 0 : channel.stopListening(eventId);
            channel === null || channel === void 0 ? void 0 : channel.subscription.unbind('pusher:subscription_error', onPusherChannelErrorHandle);
            channel === null || channel === void 0 ? void 0 : channel.subscription.unbind('pusher:subscription_succeeded', onPusherSubscribeHandle);
        }
        if (echoSession.echo) {
            echoSession.echo.connector.pusher.connection.unbind('error', onPusherErrorHandle);
            echoSession.echo.connector.pusher.connection.unbind('state_change', onPusherStateChangeHandle);
        }
    };
    const connect = () => {
        disconnect();
        if (!channelId || !eventId || channelId.includes('undefined')) {
            return;
        }
        getEchoSession()
            .then(() => {
            /* Pusher connection errors as a whole (like bad config) */
            echoSession.echo.connector.pusher.connection.bind('error', onPusherErrorHandle);
            echoSession.echo.connector.pusher.connection.bind('state_change', onPusherStateChangeHandle);
            const channelInstance = getChannel(type, channelId);
            if (!channelInstance.subscription.subscribed) {
                channelInstance.subscribe();
            }
            channelInstance
                .subscribed(onPusherSubscribeHandle)
                .listen(eventId, onPusherListenHandle)
                .error(onPusherChannelErrorHandle);
        })
            .catch((error) => onErrorRef.current(error));
    };
    const connectRef = useCurrentRef_1.useCurrentRef(connect);
    const disconnectRef = useCurrentRef_1.useCurrentRef(disconnect);
    react_1.useEffect(() => {
        if (!channelId || !eventId || channelId.includes('undefined')) {
            return;
        }
        connectRef.current();
        return () => {
            disconnectRef.current();
        };
    }, [channelId, connectRef, disconnectRef, eventId, type]);
    return {
        connect,
        disconnect,
    };
}
exports.default = useSocket;
